import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Toast from '../../components/Toast';
import ConfirmModal from '../../components/ConfirmModal';
import AssessmentEditSidebar from '../../components/admin/AssessmentEditSidebar';
import { assessmentApi } from '../../services/api';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

export default function Assessments() {
  const labels = useLabels();
  const t = useT();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateSidebar, setShowCreateSidebar] = useState(false);
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);

  const role = localStorage.getItem('role');
  const isGlobalAdmin = role === 'global_admin';
  const isAdmin = role === 'global_admin' || role === 'admin' || role === 'assessment_admin';

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  const [selectedAssessment, setSelectedAssessment] = useState(null);

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const response = await assessmentApi.getAll();
      setAssessments(response.data);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      showToast('error', t('admin.failedLoadAssessments'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssessment = () => {
    setSelectedAssessment(null);
    setShowCreateSidebar(true);
  };

  const handleDeleteAssessment = (assessment) => {
    setAssessmentToDelete(assessment);
    setConfirmOpen(true);
  };

  const confirmDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    try {
      await assessmentApi.delete(assessmentToDelete.id);
      showToast('success', t('admin.assessmentDeleted'));
      fetchAssessments();
    } catch (error) {
      showToast('error', t('admin.failedDeleteAssessment'));
    } finally {
      setConfirmOpen(false);
      setAssessmentToDelete(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-slate-100 text-slate-700';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-eucnet-grey-200 text-eucnet-grey-600';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Layout>

      <Toast
        open={toast.open}
        setOpen={(open) => setToast(prev => ({ ...prev, open }))}
        type={toast.type}
        className="fixed top-6 right-6 z-[60]"
      >
        {toast.message}
      </Toast>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-eucnet-navy">{t('admin.assessmentManagement')}</h1>
          <p className="mt-2 text-eucnet-grey-600">
            Manage national and regional {labels.isCII ? 'CII ' : ''}risk assessment projects
          </p>
        </div>

        {/* Actions Bar */}
        <div className="mb-6 flex justify-between items-center">
          <div className="text-sm text-eucnet-grey-600">
            {assessments.length} assessment{assessments.length !== 1 ? 's' : ''} found
          </div>
          {isAdmin && (
            <button
              onClick={handleCreateAssessment}
              className="bg-eucnet-blue hover:bg-eucnet-navy text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              {t('admin.createAssessment')}
            </button>
          )}
        </div>

        {/* Assessments Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-eucnet-blue"></div>
          </div>
        ) : assessments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-eucnet-grey-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-eucnet-grey-900">{t('admin.noAssessments')}</h3>
            <p className="mt-1 text-sm text-eucnet-grey-600">{t('admin.noAssessmentsDesc')}</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {assessments.map((assessment) => (
              <div
                key={assessment.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-eucnet-navy line-clamp-2">
                    {assessment.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(assessment.status)}`}>
                      {assessment.status}
                    </span>
                    {isGlobalAdmin && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedAssessment(assessment);
                            setShowCreateSidebar(true);
                          }}
                          className="text-eucnet-blue hover:text-eucnet-navy"
                          title="Edit assessment"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteAssessment(assessment)}
                          className="text-red-600 hover:text-red-700 pb-2"
                          title="Delete assessment"
                        >
                          <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                            <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                            <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {assessment.description && (
                  <p className="text-sm text-eucnet-grey-600 mb-4 line-clamp-2">
                    {assessment.description}
                  </p>
                )}

                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-eucnet-grey-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {formatDate(assessment.start_date)} - {formatDate(assessment.end_date)}
                  </div>
                  {assessment.criticality_guideline && (
                    <div className="flex items-center text-eucnet-grey-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {assessment.criticality_guideline}
                    </div>
                  )}
                  {assessment.posture_framework && (
                    <div className="flex items-center text-eucnet-grey-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0-1.657 1.343-3 3-3h4m0 0V4m0 4l-6 6m-2 2a3 3 0 11-4.243-4.243l1.414-1.414" />
                      </svg>
                      {assessment.posture_framework}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-eucnet-grey-200">
                  <button
                    onClick={() => navigate(`/admin/assessments/${assessment.id}`)}
                    className="w-full px-4 py-2 bg-eucnet-blue text-white rounded hover:bg-eucnet-navy text-sm font-medium transition-colors"
                  >
                    {t('common.viewDetails')} →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AssessmentEditSidebar
        isOpen={showCreateSidebar}
        onClose={() => setShowCreateSidebar(false)}
        assessment={selectedAssessment}
        onSaved={fetchAssessments}
        onNotify={showToast}
      />
      <ConfirmModal
        open={confirmOpen}
        title={t('admin.deleteAssessment')}
        message={assessmentToDelete ? t('admin.deleteAssessmentMsg', { name: assessmentToDelete.name }) : t('admin.deleteAssessmentGeneric')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDeleteAssessment}
        onCancel={() => {
          setConfirmOpen(false);
          setAssessmentToDelete(null);
        }}
      />
    </Layout>
  );
}
